import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MdMenuBook } from 'react-icons/md';
import { getCurrentUser } from 'aws-amplify/auth';

/* Color tokens as Tailwind arbitrary values:
   Navy:       #12356B  (primary text/borders)
   Orange:     #DD6832  (accent/CTA)
   Green:      #367A25  (primary CTA)
   GreenDark:  #2d6820  (hover)
   OrangeDark: #c4562a  (hover)
   LightGray:  #E2E5EC  (backgrounds)
   GrayAlt:    #CED4DE  (borders/alt bg)
   DarkBg:     #101822  (dark overlays)
*/

const roles = [
    {
        name: 'Biologist',
        icon: '/icons/biologist.png',
        desc: 'Analyze the alien flora and fauna to ensure team safety and find environmental clues on Bloomtar.',
    },
    {
        name: 'Diver',
        icon: '/icons/diver.png',
        desc: 'Navigate the treacherous deep-sea trenches, ensuring safe passage and resource gathering.',
    },
    {
        name: 'Fixer',
        icon: '/icons/fixer.png',
        desc: 'Maintain and repair critical equipment, ensuring the expedition\'s operational integrity.',
    },
    {
        name: 'Historian',
        icon: '/icons/historian.png',
        desc: 'Uncover ancient knowledge and past expedition logs to guide current mission decisions.',
    },
    {
        name: 'Hunter',
        icon: '/icons/hunter.png',
        desc: 'Track and secure elusive indigenous species, including the coveted Rainbow Ray.',
    },
];

const HomePage: React.FC = () => {
    const navigate = useNavigate();
    const [isLoggedIn, setIsLoggedIn] = useState(false);

    useEffect(() => {
        getCurrentUser().then(() => setIsLoggedIn(true)).catch(() => setIsLoggedIn(false));
    }, []);

    return (
        <div className="font-sans text-slate-900 bg-white">
            {/* Navigation */}
            <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 sm:h-20 flex items-center justify-between">
                    <img
                        src="/icons/centauri-logo.png"
                        alt="Centauri"
                        className="h-10 sm:h-14 object-contain"
                    />

                    <button
                        onClick={() => navigate(isLoggedIn ? '/login/teacher' : '/login')}
                        className="text-sm font-semibold px-5 py-2.5 rounded-lg border-2 border-[#12356B] text-[#12356B] hover:bg-[#E2E5EC] transition-colors"
                    >
                        {isLoggedIn ? 'Go to Dashboard' : 'Teacher Login'}
                    </button>
                </div>
            </nav>

            {/* Hero Section */}
            <main className="pt-24 sm:pt-28 pb-12 sm:pb-16 relative overflow-hidden bg-gradient-to-br from-[#E2E5EC] via-white to-[#CED4DE]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16 items-center">
                    <div className="space-y-6 sm:space-y-8">
                        <div className="inline-flex items-center gap-2.5 px-4 py-1.5 rounded-full border border-[#367A25]/40 bg-[#101822] text-xs font-bold tracking-widest uppercase text-[#367A25]">
                            <span className="relative flex h-2.5 w-2.5">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#367A25] opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#367A25]"></span>
                            </span>
                            Mission Status: <span className="text-white ml-0.5">Active</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold leading-[1.1] tracking-tight text-[#12356B]">
                            Centauri: <span className="italic text-[#DD6832]">Leadership</span> Simulation
                        </h1>
                        <p className="text-sm sm:text-base text-slate-600 max-w-lg leading-relaxed">
                            Embark on an interstellar leadership journey to the aquatic depths of Bloomtar. Master teamwork, crisis management, and strategic thinking in a high-stakes educational environment.
                        </p>
                        <div className="flex flex-col sm:flex-row md:flex-col lg:flex-row gap-3 sm:gap-4">
                            <button
                                onClick={() => navigate('/join')}
                                className="text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base shadow-lg transition-all flex items-center justify-center gap-2 group bg-[#367A25] hover:bg-[#2d6820]"
                                style={{ boxShadow: '0 10px 25px rgba(54, 122, 37, 0.33)' }}
                            >
                                Join a Game (Student)
                                <span className="group-hover:translate-x-1 transition-transform">&rarr;</span>
                            </button>
                            <button
                                onClick={() => navigate(isLoggedIn ? '/login/teacher' : '/login')}
                                className="px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base transition-all border-2 border-[#12356B] text-[#12356B] bg-white hover:bg-[#E2E5EC]"
                            >
                                {isLoggedIn ? 'Go to Dashboard' : 'Teacher Login'}
                            </button>
                        </div>
                    </div>

                    {/* Hero Image */}
                    <div className="relative mt-4 md:mt-0">
                        <div className="relative z-10 rounded-2xl overflow-hidden border border-[#12356B] shadow-2xl">
                            <img
                                className="w-full h-[280px] sm:h-[400px] lg:h-[500px] object-cover"
                                alt="Futuristic high-tech spaceship window showing an aquatic planet"
                                src="https://lh3.googleusercontent.com/aida-public/AB6AXuAQQtUCftCJ9xzBOTqYiTGsnBxuwaQEWHxUprvDLIqmd-wu6OhR7mRo9AlJ92B8AVwwlBK7Y1R7k0CQxZSCak3031QLu5Z_qEcWcvLKNE8-ZY_5fCBURGoQTq--jZNAHyjCmAvXf6iCOzvYuDH3lHzhRVWtxW7grmfC01g0Jtei50ZVOOtMC0XBuiW5ob7Tl0PcKn_SLVGzFDwGb4z1sTkK5r7rC7p3gwnaSWEz4K4okZGeev5KYOoAmwK2grE4ICapq1PHIPsi3lY8"
                                loading="eager"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-[#101822]/80 via-transparent to-transparent"></div>
                            {/* HUD Overlays */}
                            <div className="absolute top-4 sm:top-6 left-4 sm:left-6 right-4 sm:right-6 flex justify-between items-start pointer-events-none">
                                <div className="backdrop-blur-sm p-2 sm:p-3 rounded-lg border border-white/10 text-[9px] sm:text-[10px] font-mono uppercase tracking-tighter bg-[#101822]/40 text-[#DD6832]">
                                    Coordinates: 42.091 // Bloomtar Prime
                                </div>
                                <div className="backdrop-blur-sm p-2 sm:p-3 rounded-lg border border-white/10 text-[9px] sm:text-[10px] font-mono uppercase tracking-tighter bg-[#101822]/40 text-[#367A25]">
                                    Life Support: 100%
                                </div>
                            </div>
                            <div className="absolute bottom-4 sm:bottom-6 left-4 sm:left-6 right-4 sm:right-6">
                                <div className="w-full bg-white/10 h-1 rounded-full overflow-hidden">
                                    <div className="h-full w-2/3 bg-[#367A25]"></div>
                                </div>
                                <div className="flex justify-between mt-2 text-[9px] sm:text-[10px] text-white/60 font-mono uppercase">
                                    <span>Mission Progress</span>
                                    <span>67% Sync</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* About the Mission Section */}
            <section className="py-14 sm:py-20 bg-white relative">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="text-center max-w-3xl mx-auto mb-10 sm:mb-14 space-y-4">
                        <h2 className="text-sm font-bold tracking-[0.3em] uppercase text-[#DD6832]">The Mission Objective</h2>
                        <h3 className="text-3xl sm:text-4xl font-bold text-[#12356B]">Secure the Rainbow Ray</h3>
                        <p className="text-slate-500 text-base sm:text-lg">
                            The survival of our home star system depends on the retrieval of the Rainbow Ray, a bioluminescent energy source hidden within the crystalline reefs of Bloomtar. Your team must coordinate across five specialized roles.
                        </p>
                    </div>

                    {/* Role cards - icons overflow top edge */}
                    <div className="flex flex-wrap justify-center gap-x-5 gap-y-16 sm:gap-x-6 sm:gap-y-18 pt-14 sm:pt-16">
                        {roles.map((role) => (
                            <div
                                key={role.name}
                                className="group relative pt-12 sm:pt-14 px-5 sm:px-6 pb-5 sm:pb-6 bg-white rounded-xl border border-[#CED4DE] hover:border-[#12356B] transition-all hover:shadow-lg w-full sm:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] xl:w-[calc(20%-20px)] flex flex-col"
                            >
                                <img
                                    src={role.icon}
                                    alt={role.name}
                                    className="w-20 h-20 sm:w-24 sm:h-24 object-contain absolute -top-10 sm:-top-12 left-5 sm:left-6 drop-shadow-lg"
                                />
                                <h4 className="text-lg sm:text-xl font-bold mb-2 text-[#12356B]">{role.name}</h4>
                                <p className="text-sm text-gray-600 leading-relaxed flex-1">{role.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Visual Divider / CTA Banner */}
            <section
                className="w-full py-16 sm:py-24 relative overflow-hidden bg-slate-900 bg-cover bg-center"
                style={{
                    backgroundImage: `url('/images/alien-ocean-floor.png')`,
                }}
            >
                <div className="absolute inset-0 bg-slate-900/70"></div>
                <div className="relative z-10 flex flex-col items-center justify-center text-center px-4">
                    <h3 className="text-white text-2xl sm:text-3xl lg:text-4xl font-bold italic tracking-widest uppercase mb-4">
                        Experience the Unknown
                    </h3>
                    <p className="text-white/70 text-sm sm:text-base max-w-md mb-8">
                        Dive into an immersive sci-fi simulation where every decision shapes your crew's fate.
                    </p>
                    <button
                        onClick={() => navigate('/join')}
                        className="text-white px-8 py-3 rounded-xl font-bold text-base sm:text-lg transition-all bg-[#DD6832] hover:bg-[#c4562a] shadow-lg"
                    >
                        Start Your Mission
                    </button>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-10 sm:py-16 border-t border-[#CED4DE] bg-[#E2E5EC]">
                <div className="max-w-7xl mx-auto px-4 sm:px-6">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 md:gap-12 mb-8 sm:mb-12">
                        <div className="sm:col-span-2 space-y-4 sm:space-y-6">
                            <img
                                src="/icons/centauri-logo.png"
                                alt="Centauri"
                                className="h-12 sm:h-16 object-contain"
                            />
                            <p className="text-slate-500 max-w-xs text-sm">
                                Leading the next generation of leadership training through immersive science-fiction simulations. Built for modern classrooms.
                            </p>
                        </div>
                        <div>
                            <h5 className="font-bold mb-4 sm:mb-6 uppercase text-xs tracking-widest text-[#12356B]">Resources</h5>
                            <ul className="space-y-3 sm:space-y-4 text-sm text-gray-600">
                                <li><a className="hover:text-[#DD6832] transition-colors flex items-center gap-2" href="#">
                                    <MdMenuBook className="text-base" /> Centauri Handbook
                                </a></li>
                            </ul>
                        </div>
                        <div>
                            <h5 className="font-bold mb-4 sm:mb-6 uppercase text-xs tracking-widest text-[#12356B]">Legal</h5>
                            <ul className="space-y-3 sm:space-y-4 text-sm text-gray-600">
                                <li><a className="hover:text-[#DD6832] transition-colors" href="#">Privacy Policy</a></li>
                                <li><a className="hover:text-[#DD6832] transition-colors" href="#">Terms of Service</a></li>
                                <li><a className="hover:text-[#DD6832] transition-colors" href="#">Support</a></li>
                            </ul>
                        </div>
                    </div>
                    <div className="pt-6 sm:pt-8 border-t border-slate-300">
                        <p className="text-xs text-slate-400 font-medium text-center sm:text-left">
                            &copy; {new Date().getFullYear()} Centauri Simulation. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HomePage;
